import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Toolbar,
  Typography,
  Chip,
  Badge,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useUser } from '../../hooks/useUser';
import { useFeatureBadge } from '../../hooks/useFeatureBadge';

interface NavItem {
  label: string;
  to: string;
  clerkOnly?: boolean;
  featureBadgeKey?: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: '工事一覧', to: '/construction' },
  { label: '工事登録', to: '/construction/new' },
  { label: '日報一覧', to: '/daily-report' },
  { label: '請求書依頼', to: '/invoice-request' },
  { label: '売上管理', to: '/sales', clerkOnly: true },
  { label: 'スケジュール管理', to: '/schedule' },
  { label: '客先検索', to: '/customers', featureBadgeKey: 'customer_search_v1' },
];

const Navigation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userInfo } = useUser();

  const [drawerOpen, setDrawerOpen] = useState(false);

  const customerSearchBadge = useFeatureBadge('customer_search_v1');

  const isClerk = (): boolean => userInfo?.user_type === 'clerk';

  const visibleItems = NAV_ITEMS.filter((item) => !item.clerkOnly || isClerk());

  const getBadge = (item: NavItem) => {
    if (item.featureBadgeKey === 'customer_search_v1') return customerSearchBadge;
    return null;
  };

  const hasUnseenFeature = visibleItems.some((item) => getBadge(item)?.isNew);

  if (isMobile) {
    return (
      <>
        <Box
          sx={{
            py: 1,
            px: 1,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Badge
            color="error"
            badgeContent="NEW"
            invisible={!hasUnseenFeature}
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.55rem',
                height: 16,
                minWidth: 28,
                padding: '0 4px',
                fontWeight: 'bold',
              },
            }}
          >
            <IconButton
              aria-label="メニューを開く"
              onClick={() => setDrawerOpen(true)}
              size="small"
              sx={{ p: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          </Badge>
        </Box>

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{ sx: { width: 260 } }}
        >
          <Toolbar
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: 1,
              borderColor: 'divider',
              minHeight: 56,
            }}
          >
            <Typography variant="subtitle1" component="span">
              メニュー
            </Typography>
            <IconButton
              aria-label="メニューを閉じる"
              onClick={() => setDrawerOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>

          <List>
            {visibleItems.map((item, index) => {
              const badge = getBadge(item);
              return (
                <Box key={item.to}>
                  {index > 0 && <Divider />}
                  <ListItem disablePadding>
                    <ListItemButton
                      component={RouterLink}
                      to={item.to}
                      onClick={() => {
                        setDrawerOpen(false);
                        badge?.markSeen();
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{item.label}</span>
                            {badge?.isNew && (
                              <Chip
                                label="NEW"
                                color="error"
                                size="small"
                                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold' }}
                              />
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </Box>
              );
            })}
          </List>
        </Drawer>
      </>
    );
  }

  return (
    <Box
      sx={{
        py: 1.5,
        px: 2,
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        flexWrap: 'wrap',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-x',
        overflowX: 'auto',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          height: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '4px',
        },
        '& .MuiButton-root': {
          minWidth: 'auto',
          px: 1.5,
          py: 0.5,
          fontSize: '0.9rem',
          mr: 1,
          mb: 0.5,
        },
      }}
    >
      {visibleItems.map((item) => {
        const badge = getBadge(item);
        return (
          <Button
            key={item.to}
            component={RouterLink}
            to={item.to}
            onClick={() => badge?.markSeen()}
            endIcon={
              badge?.isNew ? (
                <Chip
                  label="NEW"
                  color="error"
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold' }}
                />
              ) : undefined
            }
          >
            {item.label}
          </Button>
        );
      })}
    </Box>
  );
};

export default Navigation;
